<script setup lang="ts">
import type { NuxtError } from '#app';

type ManagedStatusCode = 500 | 404;

// PROPS
interface Props {
    error: NuxtError | null;
}

const { error = null } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { message, stacktrace } = useDevelopmentError();
const { trackPageView, trackLogState } = useTracking();
const { phones } = useFleequidContacts();

// DATA
const status = computed<ManagedStatusCode | 'default'>(() => (<ManagedStatusCode[]>[500, 404]).find(item => item === error?.status) || 'default');

// HANDLERS
const handleClick = (): Promise<void> => clearError({ redirect: '/' });

// LIFECYCLE
onMounted(() => {
    if (status.value === 404) {
        trackPageView();
        trackLogState();
    }
});
</script>

<template>
    <NuxtLayout name="default">
        <BaseWidthContainer class="flex flex-1 flex-col justify-center">
            <section class="py-8">
                <div class="mx-auto max-w-(--breakpoint-lg) text-center">
                    <h1 class="text-primary mb-4 text-7xl font-bold tracking-tight lg:text-9xl">
                        {{ tt(`error.${status}.title`) }}
                    </h1>
                    <p class="mb-4 text-3xl font-bold tracking-tight text-neutral-900 md:text-4xl">
                        {{ tt(`error.${status}.subtitle`) }}
                    </p>
                    <p class="text-dimmed mb-4 text-lg font-light">
                        {{ tt(`error.${status}.description`) }}
                    </p>
                    <BaseI18nT v-if="status === 500" keypath="error.500.help.base" tag="div" class="mb-4 text-sm font-light">
                        <template #contactUs>
                            <BaseI18nT v-if="status === 500" keypath="error.500.help.contactUs" tag="span">
                                <template #email>
                                    <FleequidEmailLink type="customerService" />
                                </template>
                                <template #phone>
                                    <span class="inline-flex gap-1">
                                        <BasePhoneLink v-for="phone in phones" :key="phone" :phone="phone" />
                                    </span>
                                </template>
                            </BaseI18nT>
                        </template>
                    </BaseI18nT>
                    <UButton color="primary" variant="solid" size="lg" @click="handleClick">
                        {{ tt(`error.${status}.button`) }}
                    </UButton>
                </div>

                <div v-if="status === 500 && (!!message || stacktrace.length > 0)" class="mt-16">
                    <p v-if="message" class="text-left text-xl font-medium">{{ message }}</p>
                    <div v-if="stacktrace.length > 0" class="mb-5 h-auto flex-1 overflow-y-auto rounded-t-md bg-black/5 bg-white">
                        <pre class="z-10 block text-left font-mono text-sm font-light">
                            <span v-for="(trace, index) in stacktrace" :key="index" class="block whitespace-break-spaces" :class="{'font-semibold': !trace.internal}">{{trace.text}}</span>
                        </pre>
                    </div>
                </div>
            </section>
        </BaseWidthContainer>
    </NuxtLayout>
</template>
